import { Transacao } from '../../servicos/transacoes/typeTransacao';
import { ehEntradaInterna, ehMovimentoInterno } from '../../utils/Utils';

/**
 * Lógica pura da lista de Movimentações (filtros, totais e agrupamento por
 * dia) — extraída do componente sem mudança de comportamento.
 */

export type FiltrosDaLista = {
  busca: string;
  /** 'todas' | 'D' | 'R' */
  tipo: string;
  /** Filtros MÚLTIPLOS: lista vazia = sem filtro; itens são OU entre si. */
  categoriaIds: string[];
  contaIds: string[];
  cartaoIds: string[];
  pagamentos: string[];
};

/** Lista vazia = filtro desligado; com itens, a transação precisa casar UM deles. */
const casaAlgum = (ids: string[], id?: number | null): boolean =>
  ids.length === 0 || (id != null && ids.includes(id.toString()));

/**
 * Busca case-insensitive na descrição; demais filtros por id de relacionamento.
 * Cada filtro múltiplo é OU internamente (cartão A OU B) e E entre filtros
 * (cartão A OU B, E categoria X).
 */
export const filtrarTransacoes = (transacoes: Transacao[], filtros: FiltrosDaLista): Transacao[] =>
  transacoes.filter(
    (t) =>
      (filtros.busca === '' || t.descricao?.toLowerCase().includes(filtros.busca.toLowerCase())) &&
      (filtros.tipo === 'todas' || t.tipo === filtros.tipo) &&
      casaAlgum(filtros.categoriaIds, t.categoriaId) &&
      casaAlgum(filtros.contaIds, t.contaId) &&
      casaAlgum(filtros.cartaoIds, t.cartaoCreditoId) &&
      (filtros.pagamentos.length === 0 ||
        (!!t.tipoPagamento && filtros.pagamentos.includes(t.tipoPagamento)))
  );

/**
 * Totais do conjunto filtrado. Movimentos internos ficam fora dos totais
 * (aparecem na lista com selo): pagamento de fatura não é despesa nova
 * (as compras do cartão já contam) e transferência entre contas não é
 * receita nem despesa — é o mesmo dinheiro mudando de bolso.
 */
export const totaisDaLista = (filtradas: Transacao[]): { receitas: number; despesas: number; saldo: number } => {
  const receitas = filtradas
    .filter((t) => t.tipo === 'R' && !ehEntradaInterna(t))
    .reduce((soma, t) => soma + (t.valor || 0), 0);
  const despesas = filtradas
    .filter((t) => t.tipo === 'D' && !ehMovimentoInterno(t))
    .reduce((soma, t) => soma + (t.valor || 0), 0);
  return { receitas, despesas, saldo: receitas - despesas };
};

export const chaveDia = (data: Date) => `${data.getFullYear()}-${data.getMonth()}-${data.getDate()}`;

export type GrupoDoDia = {
  chave: string;
  data: Date;
  itens: Transacao[];
  /** Receitas − despesas do dia, excluindo pagamento de fatura. */
  totalDia: number;
};

/**
 * Ordena por data desc, corta no limite (paginação incremental) e agrupa por
 * dia local, com o total do dia excluindo pagamento de fatura.
 */
export const agruparPorDia = (filtradas: Transacao[], limite: number): GrupoDoDia[] => {
  const ordenadas = [...filtradas].sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());
  const visiveis = ordenadas.slice(0, limite);
  const mapa = new Map<string, { data: Date; itens: Transacao[] }>();
  visiveis.forEach((t) => {
    const data = new Date(t.data);
    const chave = chaveDia(data);
    const grupo = mapa.get(chave);
    if (grupo) grupo.itens.push(t);
    else mapa.set(chave, { data, itens: [t] });
  });
  return Array.from(mapa.entries()).map(([chave, grupo]) => ({
    chave,
    ...grupo,
    totalDia:
      grupo.itens
        .filter((t) => t.tipo === 'R' && !ehEntradaInterna(t))
        .reduce((soma, t) => soma + (t.valor || 0), 0) -
      grupo.itens
        .filter((t) => t.tipo === 'D' && !ehMovimentoInterno(t))
        .reduce((soma, t) => soma + (t.valor || 0), 0)
  }));
};
